import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Heart, MessageCircle, Send, Shield, Trash2 } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import EscanEatScore from '../products/ScanEatScore';
import { base44 } from '@/api/base44Client';
import { notifyCommentReply, notifyPostLike } from '@/components/notifications/NotificationHelper';
import { awardPoints, POINT_VALUES } from '@/components/loyalty/PointsHelper';

export default function PostDetail({ 
  post, 
  product,
  comments = [],
  onClose, 
  onLike,
  onComment,
  onDeletePost,
  onDeleteComment,
  currentUser,
  isLiked 
}) {
  const [commentText, setCommentText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim()) return;
    
    setIsSubmitting(true);
    const newComment = await onComment(post.id, commentText);
    
    // Award points for commenting
    if (currentUser?.id) {
      await awardPoints(currentUser.id, 'comment', POINT_VALUES.comment, `Comentó en: ${post.title}`, post.id);
    }

    // Notify post author about new comment (if it's not their own post)
    if (post.created_by && post.created_by !== currentUser?.id) {
      await notifyCommentReply({
        commentAuthorId: post.created_by,
        postTitle: post.title,
        replierName: currentUser?.full_name || 'Alguien',
        postId: post.id
      });
    }

    setCommentText('');
    setIsSubmitting(false);
  };

  const isAuthor = currentUser?.id === post.created_by;
  const isAdmin = currentUser?.role === 'admin';

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, y: 100 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 100 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white font-semibold">
                {post.author_name?.[0]?.toUpperCase()}
              </div>
              <div>
                <p className="font-medium text-gray-900">{post.author_name}</p>
                <p className="text-xs text-gray-400">
                  {format(new Date(post.created_date), "d 'de' MMMM, yyyy", { locale: es })}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-3">{post.title}</h2>
            <p className="text-gray-700 whitespace-pre-wrap mb-6">{post.content}</p>

            {/* Associated Product */}
            {product && (
              <div className="mb-6 p-4 bg-gray-50 rounded-xl">
                <p className="text-sm font-medium text-gray-500 mb-3">Producto relacionado</p>
                <div className="flex gap-4">
                  {product.image_url && (
                    <img 
                      src={product.image_url} 
                      alt={product.name}
                      className="w-20 h-20 rounded-lg object-cover"
                    />
                  )}
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900">{product.name}</p>
                    {product.brand && (
                      <p className="text-sm text-gray-500">{product.brand}</p>
                    )}
                    {product.scaneat_score !== undefined && (
                      <div className="mt-2">
                        <EscanEatScore 
                          score={product.scaneat_score} 
                          trafficLight={product.traffic_light}
                          size="small"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center gap-4 pb-6 mb-6 border-b">
              <button
                onClick={async () => {
                  await onLike(post.id);
                  
                  // Notify post author about like (if it's not their own post and it's a new like)
                  if (post.created_by && post.created_by !== currentUser?.id && !isLiked) {
                    await notifyPostLike({
                      postAuthorId: post.created_by,
                      postTitle: post.title,
                      likerName: currentUser?.full_name || 'Alguien',
                      postId: post.id
                    });
                  }
                }}
                className={`flex items-center gap-2 transition-colors ${
                  isLiked ? 'text-red-500' : 'text-gray-400 hover:text-red-500'
                }`}
              >
                <Heart className={`w-5 h-5 ${isLiked ? 'fill-current' : ''}`} />
                <span className="font-medium">{post.likes_count || 0}</span>
              </button>
              
              <div className="flex items-center gap-2 text-gray-400">
                <MessageCircle className="w-5 h-5" />
                <span className="font-medium">{comments.length}</span>
              </div>

              {(isAuthor || isAdmin) && (
                <button
                  onClick={() => onDeletePost(post.id)}
                  className="ml-auto text-red-500 hover:text-red-600 flex items-center gap-2 text-sm"
                >
                  <Trash2 className="w-4 h-4" />
                  Eliminar
                </button>
              )}
            </div>

            {/* Comments */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-4">
                Comentarios ({comments.length})
              </h3>

              {comments.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Sin comentarios aún</p>
                  <p className="text-sm">Sé el primero en comentar</p>
                </div>
              ) : (
                <div className="space-y-4 mb-6">
                  {comments.map((comment) => {
                    const isCommentAuthor = currentUser?.id === comment.created_by;
                    return (
                      <motion.div
                        key={comment.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-gray-50 rounded-xl p-4"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gray-400 to-gray-600 flex items-center justify-center text-white text-sm font-semibold">
                              {comment.author_name?.[0]?.toUpperCase()}
                            </div>
                            <div>
                              <p className="font-medium text-sm text-gray-900">{comment.author_name}</p>
                              <p className="text-xs text-gray-400">
                                {format(new Date(comment.created_date), "d MMM", { locale: es })}
                              </p>
                            </div>
                          </div>
                          
                          {(isCommentAuthor || isAdmin) && (
                            <button
                              onClick={() => onDeleteComment(comment.id)}
                              className="text-red-500 hover:text-red-600"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                        
                        {comment.status === 'pending' && (
                          <div className="flex items-center gap-2 mb-2 text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded w-fit">
                            <Shield className="w-3 h-3" />
                            Pendiente de moderación
                          </div>
                        )}
                        
                        <p className="text-gray-700 text-sm">{comment.content}</p>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Comment Form */}
          <div className="p-6 border-t bg-gray-50 flex-shrink-0">
            <div className="flex items-center gap-2 mb-3 text-xs text-amber-700 bg-amber-50 px-3 py-2 rounded-lg">
              <Shield className="w-4 h-4" />
              <p>Los comentarios son moderados. No brindes consejos médicos.</p>
            </div>
            <form onSubmit={handleSubmitComment} className="flex gap-3">
              <Textarea
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder="Escribe un comentario..."
                className="flex-1 min-h-[60px] resize-none"
                maxLength={300}
              />
              <Button
                type="submit"
                disabled={!commentText.trim() || isSubmitting}
                className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
              >
                <Send className="w-4 h-4" />
              </Button>
            </form>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}